import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "agentic-code-intelligence",
  kind: "horizontal",
  title: "Agentic Software Engineering & Code Intelligence Systems",
  shortTitle: "Agentic Coding",
  status: "draft",
  tier: "exploring",
  seoTitle: "Agentic Software Engineering & Code Intelligence Systems | GA",
  seoDescription:
    "Deterministic AI agent workflows, automated AST code migrations, test generation, and intelligent repository maintenance for complex software codebases.",
  summary:
    "Deterministic AI agent workflows and AST-level code intelligence that automate large-scale refactoring, legacy framework migrations, and test suite generation.",
  image: "/images/blog/deterministic-controls-ai-agents-hero.webp",
  relatedCapabilitySlugs: [
    "intelligent-systems",
    "software-architecture-engineering",
  ],
  relatedIndustrySlugs: [
    "enterprise",
    "healthcare",
    "energy",
    "military",
    "media-entertainment",
  ],
  relatedPostSlugs: [
    "deterministic-controls-ai-agents",
    "state-machine-verification-ai-pipelines",
  ],
  engineering: [
    "Deterministic, state-machine-controlled AI coding agents operating with strict tool permissions",
    "Abstract Syntax Tree (AST) code analysis, codemod generation, and multi-file semantic refactoring",
    "Automated regression test synthesis, fuzzing harnesses, and boundary condition validation",
    "Legacy language and framework migration pipelines (Java, C++, COBOL, Python 2 to 3, Angular to React)",
    "Repository knowledge graph modeling indexing symbols, dependencies, and architectural constraints",
    "Human review gates and automated CI verification guaranteeing generated code compiles and passes tests",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Unconstrained Coding Bots Create Subtle Technical Debt",
    },
    {
      type: "paragraph",
      text: "Generic AI code completion tools generate plausible-looking snippets that often introduce subtle race conditions, break obscure architectural invariants, or hallucinate deprecated internal APIs. When applied to enterprise-scale refactoring or multi-million-line codebases, naive copy-paste generation accelerates code rot rather than solving it.",
    },
    {
      type: "heading",
      level: 2,
      text: "State-Machine Governed Agentic Pipelines",
    },
    {
      type: "paragraph",
      text: "Real software engineering requires deterministic control loops: reading dependency graphs, parsing ASTs, executing lint rules, running targeted unit tests, and verifying compiler outputs. We engineer agentic coding systems governed by formal state machines. The AI agent proposes structured code modifications, but deterministic linters, type checkers, and test runners serve as unyielding gates that reject any invalid state before human PR review.",
    },
    {
      type: "heading",
      level: 2,
      text: "Large-Scale Migrations and Semantic Refactoring",
    },
    {
      type: "paragraph",
      text: "Massive framework upgrades or API migrations across hundreds of microservices stall because manual code changes take years. Our code intelligence pipelines combine semantic symbol indexing with targeted LLM transformations to execute multi-repository migrations systematically, delivering verified pull requests with comprehensive test coverage.",
    },
  ],
  phases: {
    study: {
      focus:
        "Audit repository architecture, build tooling, dependency graphs, test coverage, and target refactoring or migration goals.",
      produces: [
        "Codebase complexity and architectural dependency audit",
        "Migration scope and AST pattern catalog",
        "Test harness and automated validation gate requirements",
        "Agent tool permissions and security sandboxing specification",
      ],
      fit: {
        good: [
          "Enterprises managing large codebases facing major framework migrations, API upgrades, or tech-debt backlogs",
          "Engineering organizations requiring verifiable, automated test generation and strict PR quality gates",
        ],
        poor: [
          "Teams seeking unreviewed autonomous code deployment directly to production without testing",
          "Codebases lacking automated build scripts, CI pipelines, or minimal test harnesses",
        ],
      },
    },
    "initial-design": {
      focus:
        "Design repository semantic index, AST codemod patterns, agent verification state machines, and CI integration hooks.",
      produces: [
        "Repository symbol and dependency knowledge graph",
        "State-machine agent orchestration and verification architecture",
        "Custom AST rule specifications and migration prompt harnesses",
        "Automated sandbox execution and test execution container specs",
      ],
    },
    "detailed-design": {
      focus:
        "Implement agentic refactoring workers, validation harnesses, diff review dashboards, and PR automation workflows.",
      produces: [
        "Production agentic coding service integrated with GitHub/GitLab",
        "Automated regression test generation and coverage verification module",
        "Interactive diff review and human-approval queue interface",
        "Multi-repository batch migration runner with rollback capabilities",
      ],
    },
    implementation: {
      focus:
        "Deploy agentic coding pipelines into CI/CD, execute pilot migrations, and train engineering teams on agent governance.",
      produces: [
        "Deployed enterprise code intelligence infrastructure",
        "Executed pilot migration PRs with passing automated test suites",
        "Developer guidelines for state-machine agent governance and prompt maintenance",
        "Continuous code quality and agent performance monitoring dashboard",
      ],
    },
  },
};
